import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { debounce } from 'lodash';
import ReactSlider from 'react-slider';
import ScmAssigneePicker from '../components/ScmAssigneePicker';
import { isStalling } from '../constants/cartAging';
import './QuoteCartPage.css';

const fmt = (n) =>
    n != null
        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
        : '—';

const ProductRow = ({ product, onAdd }) => {
    const price = product.marketOperatingPrice || product.mrp;
    return (
        <div className="qcp-product-row">
            <div className="qcp-product-row__img">
                {product.thumbnailUrl
                    ? <img src={product.thumbnailUrl} alt={product.name} />
                    : <span>📦</span>}
            </div>
            <div className="qcp-product-row__info">
                <div className="qcp-product-row__name">{product.name}</div>
                <div className="qcp-product-row__meta">
                    <span className="qcp-product-row__cat">{product.brandName || product.category}</span>
                    <span className="qcp-product-row__price">{fmt(price)}</span>
                </div>
            </div>
            <button className="qcp-product-row__add" onClick={() => onAdd(product)}>＋ Add</button>
        </div>
    );
};

const CartItemRow = ({ item, onQtyChange, onRemove }) => {
    const itemKey = item._cartKey || String(item.id);
    const price = item.marketOperatingPrice || item.mrp;

    const currentQty = item.quantity === '' ? '' : parseInt(item.quantity, 10);
    const safeQtyForMath = currentQty || 0;

    return (
        <div className="qcp-cart-item">
            <div className="qcp-cart-item__img">
                {item.thumbnailUrl
                    ? <img src={item.thumbnailUrl} alt={item.name} />
                    : <span>📦</span>}
            </div>
            <div className="qcp-cart-item__info">
                <div className="qcp-cart-item__name">
                    {item.name}
                    {item._variantLabel && <span style={{ color: '#666', fontWeight: 400, marginLeft: 6 }}>— {item._variantLabel}</span>}
                </div>
                <div className="qcp-cart-item__price">
                    {fmt(price)} × {safeQtyForMath} = {fmt(price * safeQtyForMath)}
                </div>
            </div>
            <div className="qcp-cart-item__controls">
                <button onClick={() => onQtyChange(itemKey, Math.max(1, safeQtyForMath - 1))}>−</button>
                <input
                    type="number"
                    min="1"
                    style={{
                        width: '45px', textAlign: 'center', border: '1px solid #ccc',
                        borderRadius: '4px', height: '28px', fontSize: '0.95rem',
                        outline: 'none', appearance: 'textfield',
                        color: '#333', backgroundColor: '#fff'
                    }}
                    value={item.quantity === '' ? '' : safeQtyForMath}
                    onChange={(e) => {
                        if (e.target.value === '') {
                            onQtyChange(itemKey, '');
                        } else {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val)) onQtyChange(itemKey, val);
                        }
                    }}
                    onBlur={() => {
                        if (item.quantity === '' || item.quantity < 1) {
                            onQtyChange(itemKey, 1);
                        }
                    }}
                />
                <button onClick={() => onQtyChange(itemKey, safeQtyForMath + 1)}>+</button>
            </div>
            <button className="qcp-cart-item__remove" onClick={() => onRemove(itemKey)}>✕</button>
        </div>
    );
};

const QuoteCartPage = ({ initialMode = 'list', resumeDraftId = null, onDone }) => {
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const location   = useLocation();

    const [mode, setMode]             = useState(initialMode);
    const [quotes, setQuotes]         = useState([]);
    const [isLoadingQuotes, setIsLoadingQuotes] = useState(true);
    const [quotesError, setQuotesError] = useState(null);

    const [step, setStep]             = useState(1);

    // UI Drawer States
    const [isSearchOpen, setIsSearchOpen]   = useState(true);
    const [isFiltersOpen, setIsFiltersOpen] = useState(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching]     = useState(false);

    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [cartItems, setCartItems]   = useState([]);
    const [cartName, setCartName]     = useState('');
    const [quoteCreatedFor, setQuoteCreatedFor] = useState('');
    const [clientEmail, setClientEmail] = useState('');
    const [eyantraPocEmail, setEyantraPocEmail] = useState('');
    const [assignedScmEmail, setAssignedScmEmail] = useState('');
    const [emsEnquiryId, setEmsEnquiryId] = useState('');
    const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);

    // Invoice fields
    const [modeTermsOfPayment,  setModeTermsOfPayment]  = useState('');
    const [paymentTerms,        setPaymentTerms]        = useState('');
    const [suppliersRef,        setSuppliersRef]        = useState('');
    const [otherReferences,     setOtherReferences]     = useState('');
    const [buyerOrderNo,        setBuyerOrderNo]        = useState('');
    const [buyerOrderDated,     setBuyerOrderDated]     = useState('');
    const [despatchDocumentNo,  setDespatchDocumentNo]  = useState('');
    const [destination,         setDestination]         = useState('');
    const [despatchedType,      setDespatchedType]      = useState('');
    const [termsOfDelivery,     setTermsOfDelivery]     = useState('');
    const [preparedBy,          setPreparedBy]          = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError]           = useState('');
    const [createdCart, setCreatedCart] = useState(null);
    const [copied, setCopied]         = useState('');
    const [touched, setTouched]       = useState({});

    // Draft state — DB-backed
    const [draftId, setDraftId]             = useState(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [draftSavedMsg, setDraftSavedMsg] = useState('');

    const [listSearch, setListSearch] = useState('');
    const [listDateFrom, setListDateFrom] = useState('');
    const [listDateTo, setListDateTo] = useState('');

    // Draft list for the staging area
    const [drafts, setDrafts]               = useState([]);
    const [isDraftsLoading, setIsDraftsLoading] = useState(false);

    // Pre-load items staged from the local cart (internal users browsing the catalogue)
    useEffect(() => {
        const preloaded = location.state?.preloadItems;
        if (preloaded?.length > 0) {
            setMode('create');
            setCartItems(preloaded.map(item => ({
                id:                   item.id,
                _cartKey:             item._cartKey || String(item.id),
                _variantLabel:        item._variantLabel || null,
                name:                 item.name,
                thumbnailUrl:         item.thumbnailUrl || item.image || null,
                marketOperatingPrice: item.marketOperatingPrice || item.price || 0,
                mrp:                  item.mrp || 0,
                gst:                  item.gst || 0,
                quantity:             item.quantity || 1,
            })));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [categories, setCategories] = useState([]);
    const [brands, setBrands]         = useState([]);
    const [priceBounds, setPriceBounds] = useState([0, 100000]);

    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedBrand, setSelectedBrand]       = useState('');
    const [priceRange, setPriceRange]             = useState([0, 100000]);

    const itemsModifiedByUser = useRef(false);
    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (isSearching) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isSearching, hasMore]);

    const fetchQuotes = useCallback(() => {
        setIsLoadingQuotes(true);
        api.get('/quotes/all')
            .then(r => { setQuotes(r.data || []); setIsLoadingQuotes(false); })
            .catch(() => { setQuotesError('Could not load curated carts.'); setIsLoadingQuotes(false); });
    }, []);

    const fetchDrafts = useCallback(() => {
        setIsDraftsLoading(true);
        api.get('/quotes/drafts')
            .then(r => { setDrafts(r.data || []); setIsDraftsLoading(false); })
            .catch(() => { setIsDraftsLoading(false); });
    }, []);

    useEffect(() => {
        if (mode === 'list') {
            fetchQuotes();
            fetchDrafts();
        }
    }, [mode, fetchQuotes, fetchDrafts]);

    // Auto-fill POC email with the logged-in user's email
    useEffect(() => {
        if (user?.email && !eyantraPocEmail) setEyantraPocEmail(user.email);
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    // Generate cart name once when client name is first entered
    useEffect(() => {
        if (!cartName && quoteCreatedFor.trim()) {
            setCartName(generateCartName(quoteCreatedFor));
        }
    }, [quoteCreatedFor]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load draft when resumeDraftId is provided
    useEffect(() => {
        if (!resumeDraftId) return;
        api.get(`/quotes/drafts/${resumeDraftId}`)
            .then(r => {
                const d = r.data;
                setDraftId(resumeDraftId);
                if (d.items?.length && !itemsModifiedByUser.current) {
                    setCartItems(d.items.map(i => ({
                        id:                   i.id,
                        name:                 i.name,
                        thumbnailUrl:         i.thumbnailUrl,
                        marketOperatingPrice: i.marketOperatingPrice,
                        mrp:                  i.mrp,
                        gst:                  i.gst,
                        quantity:             i.quantity,
                    })));
                }
                if (d.quoteCreatedFor)        setQuoteCreatedFor(d.quoteCreatedFor);
                if (d.clientEmail)            setClientEmail(d.clientEmail);
                if (d.eyantraPocEmail)        setEyantraPocEmail(d.eyantraPocEmail);
                if (d.assignedScmEmail)       setAssignedScmEmail(d.assignedScmEmail);
                if (d.cartName)               setCartName(d.cartName);
                if (d.emsEnquiryId)           setEmsEnquiryId(d.emsEnquiryId);
                if (d.modeTermsOfPayment)     setModeTermsOfPayment(d.modeTermsOfPayment);
                if (d.paymentTerms)           setPaymentTerms(d.paymentTerms);
                if (d.suppliersRef)           setSuppliersRef(d.suppliersRef);
                if (d.otherReferences)        setOtherReferences(d.otherReferences);
                if (d.buyerOrderNo)           setBuyerOrderNo(d.buyerOrderNo);
                if (d.buyerOrderDated)        setBuyerOrderDated(d.buyerOrderDated);
                if (d.despatchDocumentNo)     setDespatchDocumentNo(d.despatchDocumentNo);
                if (d.destination)            setDestination(d.destination);
                if (d.despatchedType)         setDespatchedType(d.despatchedType);
                if (d.termsOfDelivery)        setTermsOfDelivery(d.termsOfDelivery);
                if (d.preparedBy)             setPreparedBy(d.preparedBy);
            })
            .catch(() => {
                setError('Failed to load draft. It may have been deleted.');
            });
    }, [resumeDraftId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-generate cartName from client name + date + random suffix
    const generateCartName = (clientName) => {
        if (!clientName.trim()) return '';
        const sanitized = clientName.trim()
            .replace(/[^a-zA-Z0-9\s]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .split(' ').slice(0, 2).join(' ');
        const d = new Date();
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        const ms = String(d.getTime()).slice(-5);
        return `${sanitized}_${dd}${mm}${yyyy}_${ms}`;
    };

    // Build the common payload used for both save-draft and submit
    const buildPayload = useCallback(() => {
        const finalCartName = cartName.trim() || generateCartName(quoteCreatedFor);
        return {
            cartName:           finalCartName,
            quoteCreatedFor:    quoteCreatedFor.trim() || null,
            clientEmail:        clientEmail.trim()     || null,
            eyantraPocEmail:    eyantraPocEmail.trim() || null,
            assignedScmEmail:   assignedScmEmail.trim() || null,
            emsEnquiryId:       emsEnquiryId.trim()   || null,
            items:              cartItems.map(i => ({ productId: i.id, quantity: Math.max(1, parseInt(i.quantity, 10) || 1) })),
            modeTermsOfPayment: modeTermsOfPayment.trim() || null,
            paymentTerms:       paymentTerms.trim()       || null,
            suppliersRef:       suppliersRef.trim()       || null,
            otherReferences:    otherReferences.trim()    || null,
            buyerOrderNo:       buyerOrderNo.trim()       || null,
            buyerOrderDated:    buyerOrderDated.trim()    || null,
            despatchDocumentNo: despatchDocumentNo.trim() || null,
            destination:        destination.trim()        || null,
            despatchedType:     despatchedType.trim()     || null,
            termsOfDelivery:    termsOfDelivery.trim()    || null,
            preparedBy:         preparedBy.trim()         || null,
        };
    }, [cartItems, cartName, quoteCreatedFor, clientEmail, eyantraPocEmail, assignedScmEmail, emsEnquiryId,
        modeTermsOfPayment, paymentTerms, suppliersRef, otherReferences, buyerOrderNo,
        buyerOrderDated, despatchDocumentNo, destination, despatchedType, termsOfDelivery, preparedBy]);

    const saveDraft = async () => {
        setIsSavingDraft(true);
        setDraftSavedMsg('');
        try {
            const payload = buildPayload();
            let res;
            if (draftId) {
                res = await api.put(`/quotes/drafts/${draftId}`, payload);
            } else {
                res = await api.post('/quotes/drafts', payload);
                setDraftId(res.data.id);
                // Update cartName with whatever the server assigned
                if (res.data.cartName) setCartName(res.data.cartName);
            }
            setDraftSavedMsg('Draft saved ✓');
            setTimeout(() => setDraftSavedMsg(''), 2500);
        } catch {
            setDraftSavedMsg('Failed to save draft');
        } finally {
            setIsSavingDraft(false);
        }
    };

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [catRes, brandRes, rangeRes] = await Promise.all([
                    api.get('/categories'),
                    api.get('/brands'),
                    api.get('/products/price-range')
                ]);
                setCategories(catRes.data || []);
                setBrands(brandRes.data || []);
                if (rangeRes.data) {
                    const bounds = [Math.floor(rangeRes.data.minPrice), Math.ceil(rangeRes.data.maxPrice)];
                    setPriceBounds(bounds);
                    setPriceRange(bounds);
                }
            } catch (err) {
                console.error("Error fetching filters", err);
            }
        };
        fetchFilters();
    }, []);

    const doSearch = useCallback(
        debounce(async (params, page, isNewSearch) => {
            setIsSearching(true);
            try {
                const res = await api.get('/products', { params: { ...params, page: page, size: 15 } });
                const fetchedProducts = res.data?.content || [];

                setSearchResults(prev => isNewSearch ? fetchedProducts : [...prev, ...fetchedProducts]);
                setHasMore(!res.data?.last);
            } catch {
                if (isNewSearch) setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 400),
        []
    );

    useEffect(() => {
        if (mode === 'create' && step === 1) {
            setCurrentPage(0);
        }
    }, [searchTerm, selectedCategory, selectedBrand, priceRange, mode, step]);

    useEffect(() => {
        if (mode === 'create' && step === 1) {
            const params = {
                search: searchTerm,
                category: selectedCategory,
                brand: selectedBrand,
                minPrice: priceRange[0],
                maxPrice: priceRange[1]
            };
            doSearch(params, currentPage, currentPage === 0);
        }
    }, [searchTerm, selectedCategory, selectedBrand, priceRange, currentPage, mode, step, doSearch]);

    const addProduct = (product) => {
        itemsModifiedByUser.current = true;
        const key = String(product.id);
        setCartItems(prev => {
            const exists = prev.find(i => (i._cartKey || String(i.id)) === key);
            if (exists) return prev.map(i => (i._cartKey || String(i.id)) === key ? { ...i, quantity: i.quantity + 1 } : i);
            return [...prev, { ...product, _cartKey: key, quantity: 1 }];
        });
    };

    const updateQty  = (key, qty) => { itemsModifiedByUser.current = true; const k = String(key); setCartItems(prev => prev.map(i => (i._cartKey || String(i.id)) === k ? { ...i, quantity: qty } : i)); };
    const removeItem = (key)      => { itemsModifiedByUser.current = true; const k = String(key); setCartItems(prev => prev.filter(i => (i._cartKey || String(i.id)) !== k)); };

    const { totalBase, totalGst, totalAmount } = useMemo(() => {
        return cartItems.reduce((acc, item) => {
            const unitPriceInclGst = item.marketOperatingPriceWithGst || item.mrp || 0;
            const unitBasePrice = item.marketOperatingPrice || (item.mrp ? item.mrp / (1 + (item.gst || 0)/100) : 0);

            const qty = parseInt(item.quantity, 10) || 0;

            const itemTotalAmount = unitPriceInclGst * qty;
            const itemBasePriceTotal = unitBasePrice * qty;
            const itemGstAmount = itemTotalAmount - itemBasePriceTotal;

            return {
                totalBase: acc.totalBase + itemBasePriceTotal,
                totalGst: acc.totalGst + itemGstAmount,
                totalAmount: acc.totalAmount + itemTotalAmount
            };
        }, { totalBase: 0, totalGst: 0, totalAmount: 0 });
    }, [cartItems]);

    const handleSubmit = async () => {
        const finalCartName = cartName.trim() || generateCartName(quoteCreatedFor);
        if (!cartName.trim()) setCartName(finalCartName);

        const requiredFields = ['quoteCreatedFor', 'clientEmail', 'eyantraPocEmail'];
        setTouched(prev => ({ ...prev, ...Object.fromEntries(requiredFields.map(f => [f, true])) }));
        const hasFieldErrors = requiredFields.some(f => getFieldError(f) !== null);
        if (hasFieldErrors) {
            setTimeout(() => {
                const firstError = document.querySelector('.qcp-field--error');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const input = firstError.querySelector('input, textarea, select');
                    if (input) input.focus();
                }
            }, 50);
            return;
        }
        if (cartItems.length === 0) { setError('Add at least one product to the cart before submitting.'); return; }
        setError('');
        setIsSubmitting(true);
        try {
            const payload = {
                ...buildPayload(),
                cartName: finalCartName,
            };

            let cartId, resolvedCartName;

            if (draftId) {
                // Publish the draft — validates + changes status to CART
                const res = await api.post(`/quotes/drafts/${draftId}/publish`, payload);
                cartId = res.data.cartId;
                resolvedCartName = res.data.cartName;
            } else {
                // Direct creation path (no draft)
                const res = await api.post('/quotes/create', payload);
                cartId = res.data.cartId;
                resolvedCartName = finalCartName;
            }

            setDraftId(null);
            setCreatedCart({ cartName: resolvedCartName, id: cartId });
            setStep(3);
        } catch (e) {
            setError(e.response?.data?.error || e.response?.data?.message || 'Failed to create quote cart. The cart name may already exist.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteDraft = async (id) => {
        try {
            await api.delete(`/quotes/drafts/${id}`);
            setDrafts(prev => prev.filter(d => d.id !== id));
        } catch {
            // silently ignore — draft may have already been deleted
        }
    };

    const markTouched = (field) => setTouched(prev => ({ ...prev, [field]: true }));

    const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

    const getFieldError = (field) => {
        if (field === 'quoteCreatedFor')
            return !quoteCreatedFor.trim()
                ? "Enter the client's name or company — e.g. 'Ravi Kumar' or 'Acme Technologies Pvt. Ltd.'"
                : null;
        if (field === 'clientEmail') {
            if (!clientEmail.trim()) return "Client email is required — they'll use this to log in and view the cart";
            if (!isValidEmail(clientEmail)) return "Check the email format — e.g. ravi@acmecorp.com";
            return null;
        }
        if (field === 'eyantraPocEmail') {
            if (!eyantraPocEmail.trim()) return "Add the eYantra team member handling this account — e.g. priya@eyantra.com";
            if (!isValidEmail(eyantraPocEmail)) return "Use a valid work email — e.g. priya@eyantra.com";
            return null;
        }
        return null;
    };

    const FIELD_INFO = {
        quoteCreatedFor: "Client or company this cart is being prepared for",
        clientEmail:     "They'll log in with this email to view and collaborate on the cart",
        cartName:        "Unique identifier used internally — cannot be changed after creation",
        eyantraPocEmail: "Internal eYantra contact managing this account",
    };

    const fieldHint = (field) => {
        const err = touched[field] ? getFieldError(field) : null;
        return { error: err, info: err ? null : FIELD_INFO[field] };
    };

    const copyToClipboard = (text, key) => {
        navigator.clipboard.writeText(text);
        setCopied(key);
        setTimeout(() => setCopied(''), 2000);
    };

    const shareText = createdCart
        ? `Hi! I've prepared a curated product cart for you.\n\nCart Name: ${createdCart.cartName}\nLogin to shop.eyantra.com with your email to view, edit, and collaborate on your cart.`
        : '';

    const fmtDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    if (!user || !['ADMIN', 'CURATOR', 'EMPLOYEE'].includes(user.role)) {
        return <div className="loading-state">Access denied. eYantra staff only.</div>;
    }

    return (
        <div className="qcp-wrapper">
            <div className="qcp-header">
                <div className="qcp-header__left">
                    <div className="qcp-header__eyebrow">Cart Management</div>
                    <h1 className="qcp-header__title" style={mode === 'list' ? {
                        fontFamily: 'inherit', fontSize: '1.5rem', fontWeight: 600, letterSpacing: 'normal',
                    } : {}}>
                        {mode === 'list' ? 'Curated Carts & Analytics' : 'Create Curated Cart'}
                    </h1>
                </div>

                {mode === 'create' ? (
                    <div className="qcp-header__steps">
                        {['Add Products', 'Cart Details', 'Share'].map((label, i) => (
                            <div key={i} className={`qcp-step ${step > i + 1 ? 'done' : ''} ${step === i + 1 ? 'active' : ''}`}>
                                <div className="qcp-step__num">{step > i + 1 ? '✓' : i + 1}</div>
                                <div className="qcp-step__label">{label}</div>
                                {i < 2 && <div className="qcp-step__line" />}
                            </div>
                        ))}
                    </div>
                ) : (
                    <button className="qcp-btn qcp-btn--primary" onClick={() => { setMode('create'); setStep(1); }}>
                        ＋ Create New Cart
                    </button>
                )}
            </div>

            {mode === 'list' && (
                <div style={{ width: '100%' }}>
                    {/* ── Staging Area — DB-backed drafts ── */}
                    <div style={{ marginBottom: '1.75rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                            Staging Area
                        </div>
                        {isDraftsLoading ? (
                            <div style={{
                                background: '#f9fafb', border: '1.5px dashed #d1d5db',
                                borderRadius: '10px', padding: '0.875rem 1.25rem',
                                fontSize: '0.875rem', color: '#9ca3af',
                            }}>
                                Loading drafts…
                            </div>
                        ) : drafts.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {drafts.map(draft => (
                                    <div key={draft.id} style={{
                                        display: 'flex', alignItems: 'center', gap: '1rem',
                                        background: '#fff7ed', border: '1.5px dashed #fb923c',
                                        borderRadius: '10px', padding: '0.875rem 1.25rem', flexWrap: 'wrap',
                                    }}>
                                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>🗂️</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, color: '#c2410c', fontSize: '0.9rem' }}>
                                                {draft.quoteCreatedFor || 'No client name yet'}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#ea580c', marginTop: '2px' }}>
                                                {draft.itemCount > 0
                                                    ? <span>{draft.itemCount} item{draft.itemCount !== 1 ? 's' : ''}</span>
                                                    : <span>No items yet</span>}
                                                <span style={{ marginLeft: '8px', opacity: 0.75 }}>· Updated {fmtDate(draft.updatedAt || draft.createdAt)}</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setDraftId(draft.id);
                                                setMode('create');
                                                setStep(1);
                                            }}
                                            style={{ background: '#f97316', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', flexShrink: 0 }}
                                        >
                                            Resume
                                        </button>
                                        <button
                                            onClick={() => handleDeleteDraft(draft.id)}
                                            style={{ background: 'transparent', color: '#c2410c', border: '1px solid #fb923c', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', flexShrink: 0 }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))}
                                <button
                                    onClick={() => { setMode('create'); setStep(1); }}
                                    style={{ alignSelf: 'flex-start', background: 'transparent', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
                                >
                                    + Start New Cart
                                </button>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '1rem',
                                background: '#f9fafb', border: '1.5px dashed #d1d5db',
                                borderRadius: '10px', padding: '0.875rem 1.25rem',
                            }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>📋</div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 500, color: '#6b7280', fontSize: '0.875rem' }}>No drafts in staging</div>
                                    <div style={{ fontSize: '0.78rem', color: '#9ca3af', marginTop: '2px' }}>Save a draft while creating a cart and it will appear here.</div>
                                </div>
                                <button onClick={() => { setMode('create'); setStep(1); }} style={{ background: 'transparent', color: '#374151', border: '1.5px solid #d1d5db', borderRadius: '6px', padding: '0.45rem 1rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', flexShrink: 0 }}>
                                    + Start New Cart
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Analytics + Table ── */}
                    {(() => {
                const now = Date.now();

                const dateFilteredQuotes = quotes.filter(q => {
                    if (!q.createdAt) return true;
                    const d = new Date(q.createdAt);
                    if (listDateFrom && d < new Date(listDateFrom)) return false;
                    if (listDateTo && d > new Date(listDateTo + 'T23:59:59')) return false;
                    return true;
                });

                const totalWorkspaces    = dateFilteredQuotes.length;
                const pendingRfqs        = dateFilteredQuotes.filter(q => q.status === 'REQUESTED').length;
                const inCartStage        = dateFilteredQuotes.filter(q => q.status === 'CART').length;
                const quotesSent         = dateFilteredQuotes.filter(q => q.status === 'QUOTE').length;
                const successConversions = dateFilteredQuotes.filter(q => q.status === 'CONVERTED').length;
                // Was a third, incompatible definition of "abandoned": age since
                // creation plus never-viewed, which counted converted and lost carts
                // and ignored the stage a cart was actually sitting in. Now the same
                // shared predicate the admin screen uses, so the two agree.
                const abandonedQuotes    = dateFilteredQuotes.filter(q => isStalling(q, now));
                const abandoned          = abandonedQuotes.length;
                const totalRfqs     = pendingRfqs + quotesSent + successConversions;
                const rfqConverted  = successConversions;
                const rfqPct        = totalRfqs > 0 ? Math.round((rfqConverted / totalRfqs) * 100) : 0;

                const sumVal = (arr) => arr.reduce((s, q) => s + (q.totalValue || 0), 0);
                const totalValue     = sumVal(dateFilteredQuotes);
                const pendingValue   = sumVal(dateFilteredQuotes.filter(q => q.status === 'REQUESTED'));
                const inCartValue    = sumVal(dateFilteredQuotes.filter(q => q.status === 'CART'));
                const quotesValue    = sumVal(dateFilteredQuotes.filter(q => q.status === 'QUOTE'));
                const convertedValue = sumVal(dateFilteredQuotes.filter(q => q.status === 'CONVERTED'));
                const abandonedValue = sumVal(abandonedQuotes);

                const lowerSearch = listSearch.toLowerCase();
                const filteredQuotes = listSearch
                    ? dateFilteredQuotes.filter(q =>
                        q.cartName?.toLowerCase().includes(lowerSearch) ||
                        q.quoteCreatedFor?.toLowerCase().includes(lowerSearch) ||
                        q.createdByEmail?.toLowerCase().includes(lowerSearch) ||
                        q.clientEmail?.toLowerCase().includes(lowerSearch) ||
                        q.status?.toLowerCase().includes(lowerSearch)
                    )
                    : dateFilteredQuotes;

                const statusStyle = (s) => {
                    const map = {
                        QUOTE:      { bg: '#ede9fe', color: '#6d28d9' },
                        CART:       { bg: '#f3f4f6', color: '#374151' },
                        CONVERTED:  { bg: '#d1fae5', color: '#065f46' },
                        REQUESTED:  { bg: '#fef3c7', color: '#92400e' },
                        CLOSED_LOST:{ bg: '#fee2e2', color: '#991b1b' },
                    };
                    return map[s] || map.CART;
                };

                const copyCartLink = (q) => {
                    const url = `${window.location.origin}/#/shared-cart?id=${q.id}`;
                    navigator.clipboard.writeText(url);
                    setCopied(`link-${q.id}`);
                    setTimeout(() => setCopied(''), 2000);
                };

                const statCards = [
                    { value: totalWorkspaces,   amount: totalValue,     label: 'TOTAL WORKSPACES',       color: '#2563eb', selected: true },
                    { value: pendingRfqs,        amount: pendingValue,   label: 'PENDING RFQS',           color: '#f59e0b' },
                    { value: inCartStage,        amount: inCartValue,    label: 'IN CART STAGE',          color: '#7c3aed' },
                    { value: quotesSent,         amount: quotesValue,    label: 'QUOTES SENT',            color: '#4f46e5' },
                    { value: successConversions, amount: convertedValue, label: 'SUCCESSFUL CONVERSIONS', color: '#16a34a' },
                    { value: abandoned,          amount: abandonedValue, label: 'STALLING',               color: '#dc2626' },
                ];

                return (
                    <div style={{ width: '100%' }}>
                        {isLoadingQuotes ? (
                            <div className="loading-state">Loading carts...</div>
                        ) : quotesError ? (
                            <div className="qcp-error">{quotesError}</div>
                        ) : (
                            <>
                                {/* Stats cards */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                    gap: '0.875rem', marginBottom: '1.25rem',
                                }}>
                                    {statCards.map((card) => (
                                        <div key={card.label} style={{
                                            background: '#fff',
                                            border: card.selected ? `2px solid ${card.color}` : '1.5px solid #e5e7eb',
                                            borderRadius: '12px',
                                            padding: '1.1rem 1.25rem',
                                            boxShadow: card.selected ? `0 0 0 3px ${card.color}18` : '0 1px 3px rgba(0,0,0,0.06)',
                                        }}>
                                            <div style={{ fontSize: '2rem', fontWeight: 700, color: card.color, lineHeight: 1.1 }}>
                                                {card.value}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: card.color, marginTop: '3px', opacity: 0.75 }}>
                                                {fmt(card.amount)}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', marginTop: '0.25rem', letterSpacing: '0.05em' }}>
                                                {card.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* RFQ conversion bar */}
                                {totalRfqs > 0 && (
                                    <div style={{
                                        background: '#fffbeb', border: '1px solid #fde68a',
                                        borderRadius: '12px', padding: '1rem 1.25rem',
                                        marginBottom: '1.25rem',
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#374151', marginBottom: '0.35rem' }}>
                                            <span>Total RFQs Received:</span>
                                            <strong>{totalRfqs}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#374151', marginBottom: '0.6rem' }}>
                                            <span>RFQs Converted to Quote:</span>
                                            <strong>{rfqConverted}</strong>
                                        </div>
                                        <div style={{ background: '#e5e7eb', borderRadius: '999px', height: '6px' }}>
                                            <div style={{
                                                background: '#f59e0b', borderRadius: '999px',
                                                height: '6px', width: `${rfqPct}%`,
                                                transition: 'width 0.4s ease',
                                            }} />
                                        </div>
                                    </div>
                                )}

                                {/* Date Filter */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6b7280' }}>Date Range:</span>
                                    <input
                                        type="date"
                                        value={listDateFrom}
                                        onChange={e => setListDateFrom(e.target.value)}
                                        style={{ padding: '0.4rem 0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem' }}
                                    />
                                    <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>to</span>
                                    <input
                                        type="date"
                                        value={listDateTo}
                                        onChange={e => setListDateTo(e.target.value)}
                                        style={{ padding: '0.4rem 0.6rem', border: '1.5px solid #e5e7eb', borderRadius: '6px', fontSize: '0.82rem' }}
                                    />
                                    {(listDateFrom || listDateTo) && (
                                        <button
                                            onClick={() => { setListDateFrom(''); setListDateTo(''); }}
                                            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>

                                {/* Search + count */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.875rem' }}>
                                    <div style={{ flex: 1, position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', fontSize: '0.9rem' }}>🔍</span>
                                        <input
                                            type="text"
                                            placeholder="Search by cart name, email, client, status..."
                                            value={listSearch}
                                            onChange={e => setListSearch(e.target.value)}
                                            style={{
                                                width: '100%', padding: '0.6rem 1rem 0.6rem 2.2rem',
                                                border: '1.5px solid #e5e7eb', borderRadius: '8px',
                                                fontSize: '0.875rem', outline: 'none', background: '#fff',
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                    </div>
                                    <span style={{ fontSize: '0.82rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                        {filteredQuotes.length} result{filteredQuotes.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {/* Table */}
                                {filteredQuotes.length === 0 ? (
                                    <div className="qcp-cart-empty">
                                        <div className="qcp-cart-empty__icon">📋</div>
                                        <div>{quotes.length === 0 ? 'No curated carts yet. Click "+ Create New Cart" to get started.' : 'No results match your search.'}</div>
                                    </div>
                                ) : (
                                    <div className="qcp-table-wrap">
                                        <table className="qcp-table">
                                            <thead>
                                                <tr>
                                                    <th>Cart Name</th>
                                                    <th>Status</th>
                                                    <th>Created By</th>
                                                    <th>Items</th>
                                                    <th>Created At</th>
                                                    <th>Viewers</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredQuotes.map(q => {
                                                    const ss = statusStyle(q.status);
                                                    const viewers = q.viewerLogs?.length || 0;
                                                    return (
                                                        <tr key={q.id}>
                                                            <td style={{ maxWidth: '220px' }}>
                                                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{q.cartName}</div>
                                                                {q.quoteCreatedFor && (
                                                                    <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '2px' }}>{q.quoteCreatedFor}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span style={{
                                                                    padding: '3px 10px', borderRadius: '999px',
                                                                    fontSize: '0.75rem', fontWeight: 600,
                                                                    background: ss.bg, color: ss.color,
                                                                }}>
                                                                    {q.status || 'CART'}
                                                                </span>
                                                            </td>
                                                            <td style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                                                                {q.createdByEmail || '—'}
                                                            </td>
                                                            <td style={{ textAlign: 'center' }}>{q.items?.length ?? 0}</td>
                                                            <td style={{ fontSize: '0.85rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                                                {fmtDate(q.createdAt)}
                                                            </td>
                                                            <td>
                                                                {viewers === 0 ? (
                                                                    <span style={{ fontSize: '0.82rem', color: '#9ca3af' }}>Not viewed</span>
                                                                ) : (
                                                                    <span style={{ fontSize: '0.82rem', color: '#059669' }}>{viewers} view{viewers !== 1 ? 's' : ''}</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                                    <button
                                                                        className="qcp-btn qcp-btn--primary"
                                                                        style={{ padding: '0.35rem 0.9rem', fontSize: '0.8rem' }}
                                                                        onClick={() => navigate(`/shared-cart?id=${q.id}`)}>
                                                                        View
                                                                    </button>
                                                                    <button
                                                                        className="qcp-copy-btn"
                                                                        style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                                                                        onClick={() => copyCartLink(q)}>
                                                                        {copied === `link-${q.id}` ? '✓ Copied!' : '📋 Copy Link'}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                );
            })()}
                </div>
            )}

            {mode === 'create' && (
                <>
                    <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <button className="qcp-btn qcp-btn--ghost" onClick={() => {
                            if (onDone) onDone();
                            else setMode('list');
                        }}>← Back to Carts List</button>
                        {draftId && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                background: '#f0fdf4', border: '1px solid #bbf7d0',
                                borderRadius: '6px', padding: '0.3rem 0.75rem',
                                fontSize: '0.8rem', color: '#16a34a', fontWeight: 500,
                            }}>
                                ☁ Draft saved to cloud
                            </span>
                        )}
                        {draftSavedMsg && (
                            <span style={{
                                fontSize: '0.8rem',
                                color: draftSavedMsg.includes('Failed') ? '#dc2626' : '#16a34a',
                                fontWeight: 500,
                            }}>
                                {draftSavedMsg}
                            </span>
                        )}
                    </div>

            {step === 1 && (
                <div className={`qcp-workspace ${isFiltersOpen ? 'qcp-workspace--filters-open' : ''}`}>

                    {/* 1. Left Sidebar Filters (Collapsible Drawer) */}
                    <div className={`qcp-filter-drawer ${isFiltersOpen ? 'qcp-filter-drawer--open' : ''}`}>
                        <aside className="qcp-filter-drawer__inner">
                            <div className="qcp-filter-drawer__header">
                                <div className="qcp-filter-drawer__title">Filters</div>
                                <button className="qcp-filter-clear-inline" onClick={() => {
                                    setSelectedCategory('');
                                    setSelectedBrand('');
                                    setPriceRange(priceBounds);
                                    setSearchTerm('');
                                }}>Clear All</button>
                            </div>

                            <div className="qcp-filter-section">
                                <label className="qcp-filter-label">Category</label>
                                <select
                                    className="qcp-filter-select"
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                >
                                    <option value="">All Categories</option>
                                    {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                                </select>
                            </div>

                            <div className="qcp-filter-section">
                                <label className="qcp-filter-label">Brand</label>
                                <select
                                    className="qcp-filter-select"
                                    value={selectedBrand}
                                    onChange={(e) => setSelectedBrand(e.target.value)}
                                >
                                    <option value="">All Brands</option>
                                    {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                                </select>
                            </div>

                            <div className="qcp-filter-section">
                                <label className="qcp-filter-label">Price Range</label>
                                <div className="qcp-price-info">
                                    <span>{fmt(priceRange[0])}</span>
                                    <span>{fmt(priceRange[1])}</span>
                                </div>
                                <ReactSlider
                                    className="qcp-price-slider"
                                    thumbClassName="qcp-thumb"
                                    trackClassName="qcp-track"
                                    value={priceRange}
                                    min={priceBounds[0]}
                                    max={priceBounds[1]}
                                    onChange={setPriceRange}
                                    pearling
                                    minDistance={100}
                                />
                            </div>
                        </aside>
                    </div>

                    {/* 2. Middle: Search & Results (Main Workspace) */}
                    <div className="qcp-workspace__main" style={{ display: isSearchOpen ? 'block' : 'none' }}>
                        <div className="qcp-panel">
                            <div className="qcp-panel__title" style={{marginBottom: '1rem'}}>Search Catalog</div>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                                <div className="qcp-search-wrap" style={{ flex: 1, marginBottom: 0 }}>
                                    <svg className="qcp-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search products..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className="qcp-search-input"
                                        autoFocus
                                    />
                                    {isSearching && currentPage === 0 && <div className="qcp-search-spinner" />}
                                </div>
                                <button
                                    className={`qcp-filter-toggle ${isFiltersOpen ? 'qcp-filter-toggle--active' : ''}`}
                                    onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                                    style={{ flexShrink: 0, padding: '0.6rem 1.1rem', fontSize: '0.875rem' }}
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>
                                    {isFiltersOpen ? 'Hide Filters' : 'Filters'}
                                    {(selectedCategory || selectedBrand) && (
                                        <span style={{
                                            background: '#f59e0b', color: '#fff',
                                            borderRadius: '50%', width: '16px', height: '16px',
                                            fontSize: '0.65rem', display: 'inline-flex',
                                            alignItems: 'center', justifyContent: 'center', marginLeft: '4px'
                                        }}>
                                            {[selectedCategory, selectedBrand].filter(Boolean).length}
                                        </span>
                                    )}
                                </button>
                            </div>

                            <div className="qcp-search-results">
                                {searchResults.length > 0
                                    ? searchResults.map((p, index) => {
                                        if (searchResults.length === index + 1) {
                                            return (
                                                <div ref={lastProductElementRef} key={p.id}>
                                                    <ProductRow product={p} onAdd={addProduct} />
                                                </div>
                                            );
                                        } else {
                                            return <ProductRow key={p.id} product={p} onAdd={addProduct} />;
                                        }
                                    })
                                    : (searchTerm || selectedCategory || selectedBrand) && !isSearching
                                        ? <div className="qcp-empty-search">No products match your criteria.</div>
                                        : <div className="qcp-search-hint">
                                            <div className="qcp-search-hint__icon">🔍</div>
                                            <div>Adjust filters or search to find products</div>
                                        </div>
                                }
                                {isSearching && currentPage > 0 && <div className="qcp-search-hint">Loading more...</div>}
                            </div>
                        </div>
                    </div>

                    {/* 3. Right: Cart Panel (Always Visible) */}
                    <div className="qcp-workspace__cart">
                        <div className="qcp-panel qcp-panel--cart">
                            <div className="qcp-panel__title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', width: '100%', marginBottom: '1.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                    Cart Items
                                    {cartItems.length > 0 && <span className="qcp-panel__count">{cartItems.length}</span>}
                                </div>

                                <div className="qcp-search-toolbar">
                                    <button
                                        className={`qcp-filter-toggle ${isSearchOpen ? 'qcp-filter-toggle--active' : ''}`}
                                        onClick={() => {
                                            setIsSearchOpen(!isSearchOpen);
                                            if (isSearchOpen) setIsFiltersOpen(false);
                                        }}
                                    >
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                                        {isSearchOpen ? 'Hide Search' : 'Add Products'}
                                    </button>
                                </div>
                            </div>

                            {cartItems.length === 0
                                ? (
                                    <div className="qcp-cart-empty">
                                        <div className="qcp-cart-empty__icon">🛒</div>
                                        <div>No items yet. Select "Add Products" to browse the catalog.</div>
                                    </div>
                                )
                                : (
                                    <>
                                        <div className="qcp-cart-list">
                                            {cartItems.map(item => (
                                                <CartItemRow
                                                    key={item._cartKey || item.id}
                                                    item={item}
                                                    onQtyChange={updateQty}
                                                    onRemove={removeItem}
                                                />
                                            ))}
                                        </div>

                                        <div className="qcp-cart-breakdown" style={{ marginTop: '2rem', borderTop: '1px solid #e0e0e0', paddingTop: '1.2rem' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#5f6368' }}>
                                                <span>Subtotal (Excl. GST)</span>
                                                <span>{fmt(totalBase)}</span>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#5f6368' }}>
                                                <span>Estimated GST</span>
                                                <span>{fmt(totalGst)}</span>
                                            </div>

                                            <div className="qcp-cart-total" style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.2rem', borderTop: '1px dashed #e0e0e0', paddingTop: '12px' }}>
                                                <span>Total Amount</span>
                                                <span className="qcp-cart-total__val">{fmt(totalAmount)}</span>
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                                            {(cartItems.length > 0 || quoteCreatedFor) && (
                                                <button
                                                    className="qcp-btn qcp-btn--ghost"
                                                    onClick={saveDraft}
                                                    disabled={isSavingDraft}
                                                    style={{ flex: '0 0 auto' }}
                                                >
                                                    {isSavingDraft ? 'Saving…' : '💾 Save Draft'}
                                                </button>
                                            )}
                                            <button
                                                className="qcp-btn qcp-btn--primary"
                                                style={{ flex: 1 }}
                                                onClick={() => setStep(2)}
                                            >
                                                Continue →
                                            </button>
                                        </div>
                                    </>
                                )
                            }
                        </div>
                    </div>
                </div>
            )}
                    {step === 2 && (
                        <div className="qcp-body qcp-body--centered">
                            <div className="qcp-panel qcp-panel--details">
                                <div className="qcp-panel__title">Cart Details</div>
                                <p className="qcp-panel__sub">
                                    Provide the client's email so they can log in and view this cart.
                                </p>
                                {(() => {
                                    const f1 = fieldHint('quoteCreatedFor');
                                    return (
                                        <div className={`qcp-field${f1.error ? ' qcp-field--error' : touched.quoteCreatedFor && !f1.error ? ' qcp-field--valid' : ''}`}>
                                            <label className="qcp-field__label">Prepared For <span>(Client or Company Name)</span></label>
                                            <input
                                                type="text"
                                                className="qcp-field__input"
                                                placeholder="e.g. Ravi Kumar, Acme Technologies Pvt. Ltd."
                                                value={quoteCreatedFor}
                                                onChange={e => setQuoteCreatedFor(e.target.value)}
                                                onBlur={() => markTouched('quoteCreatedFor')}
                                            />
                                            <span className={`qcp-field__hint${f1.error ? ' qcp-field__hint--error' : ''}`}>
                                                {f1.error || f1.info}
                                            </span>
                                        </div>
                                    );
                                })()}

                                {(() => {
                                    const f2 = fieldHint('clientEmail');
                                    return (
                                        <div className={`qcp-field${f2.error ? ' qcp-field--error' : touched.clientEmail && !f2.error ? ' qcp-field--valid' : ''}`}>
                                            <label className="qcp-field__label">Client Email <span>(Required for client access)</span></label>
                                            <input
                                                type="email"
                                                className="qcp-field__input"
                                                placeholder="client@company.com"
                                                value={clientEmail}
                                                onChange={e => setClientEmail(e.target.value)}
                                                onBlur={() => markTouched('clientEmail')}
                                            />
                                            <span className={`qcp-field__hint${f2.error ? ' qcp-field__hint--error' : ''}`}>
                                                {f2.error || f2.info}
                                            </span>
                                        </div>
                                    );
                                })()}

                                <div className="qcp-field">
                                    <label className="qcp-field__label">Cart ID <span>(Auto-generated)</span></label>
                                    <input
                                        type="text"
                                        className="qcp-field__input"
                                        style={{ color: '#888', background: '#f7f7f7' }}
                                        value={cartName}
                                        readOnly
                                        placeholder="Will be generated from client name"
                                    />
                                    <span className="qcp-field__hint">Generated automatically — used for internal tracking</span>
                                </div>

                                {(() => {
                                    const f5 = fieldHint('eyantraPocEmail');
                                    return (
                                        <div className={`qcp-field${f5.error ? ' qcp-field--error' : touched.eyantraPocEmail && !f5.error ? ' qcp-field--valid' : ''}`}>
                                            <label className="qcp-field__label">eYantra POC Email <span>(Internal contact)</span></label>
                                            <ScmAssigneePicker
                                                value={eyantraPocEmail}
                                                onChange={setEyantraPocEmail}
                                                placeholder="priya@eyantra.com"
                                                onBlur={() => markTouched('eyantraPocEmail')}
                                            />
                                            <span className={`qcp-field__hint${f5.error ? ' qcp-field__hint--error' : ''}`}>
                                                {f5.error || f5.info}
                                            </span>
                                        </div>
                                    );
                                })()}

                                <div className="qcp-field">
                                    <label className="qcp-field__label">Assigned SCM <span>(Optional)</span></label>
                                    <ScmAssigneePicker value={assignedScmEmail} onChange={setAssignedScmEmail} />
                                    <span className="qcp-field__hint">Assign the SCM representative who should own this enquiry</span>
                                </div>

                                {/* ── Invoice Details (collapsed by default) ── */}
                                <button
                                    type="button"
                                    onClick={() => setIsInvoiceOpen(o => !o)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        marginTop: '1.75rem', background: 'none', border: 'none',
                                        cursor: 'pointer', padding: '0.5rem 0',
                                        fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.06em',
                                        textTransform: 'uppercase', color: '#5f6368',
                                    }}
                                    aria-expanded={isInvoiceOpen}
                                >
                                    <span style={{ transition: 'transform 0.2s', display: 'inline-block', transform: isInvoiceOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                                    Invoice Details
                                    <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, opacity: 0.6 }}>(optional — most carts don't need this)</span>
                                </button>
                                {isInvoiceOpen && (
                                <div>
                                <div className="qcp-field-row">
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Mode / Terms of Payment</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="e.g. 100% Advance"
                                            value={modeTermsOfPayment} onChange={e => setModeTermsOfPayment(e.target.value)} />
                                    </div>
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Payment Terms</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="e.g. Net 30, Advance"
                                            value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} />
                                    </div>
                                </div>

                                <div className="qcp-field-row">
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Supplier's Ref.</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="Internal reference no."
                                            value={suppliersRef} onChange={e => setSuppliersRef(e.target.value)} />
                                    </div>
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Other Reference(s)</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="Any other ref."
                                            value={otherReferences} onChange={e => setOtherReferences(e.target.value)} />
                                    </div>
                                </div>

                                <div className="qcp-field-row">
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Buyer's Order No.</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="PO number"
                                            value={buyerOrderNo} onChange={e => setBuyerOrderNo(e.target.value)} />
                                    </div>
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Buyer Order Date</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="dd/mm/yyyy"
                                            value={buyerOrderDated} onChange={e => setBuyerOrderDated(e.target.value)} />
                                    </div>
                                </div>

                                <div className="qcp-field-row">
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Despatch Document No.</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="Dispatch ref. no."
                                            value={despatchDocumentNo} onChange={e => setDespatchDocumentNo(e.target.value)} />
                                    </div>
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Destination</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="City / State"
                                            value={destination} onChange={e => setDestination(e.target.value)} />
                                    </div>
                                </div>

                                <div className="qcp-field-row">
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Despatched through</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="e.g. By Road / Air / Surface"
                                            value={despatchedType} onChange={e => setDespatchedType(e.target.value)} />
                                    </div>
                                    <div className="qcp-field">
                                        <label className="qcp-field__label">Terms of Delivery</label>
                                        <input type="text" className="qcp-field__input"
                                            placeholder="e.g. Ex-Works, FOB"
                                            value={termsOfDelivery} onChange={e => setTermsOfDelivery(e.target.value)} />
                                    </div>
                                </div>

                                <div className="qcp-field">
                                    <label className="qcp-field__label">Prepared By</label>
                                    <input type="text" className="qcp-field__input"
                                        placeholder="Your name"
                                        value={preparedBy} onChange={e => setPreparedBy(e.target.value)} />
                                </div>
                                </div>
                                )}

                                <div className="qcp-summary">
                                    <div className="qcp-summary__title">Cart Summary</div>
                                    {cartItems.map(i => (
                                        <div key={i._cartKey || i.id} className="qcp-summary__row">
                                            <span>{i.name}{i._variantLabel ? ` — ${i._variantLabel}` : ''} × {i.quantity}</span>
                                            <span>{fmt((i.marketOperatingPrice || i.mrp || 0) * i.quantity)}</span>
                                        </div>
                                    ))}
                                    <div style={{ marginTop: '1rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                                        <div className="qcp-summary__row" style={{ color: '#5f6368', fontSize: '0.9rem' }}>
                                            <span>Subtotal (Excl. GST)</span>
                                            <span>{fmt(totalBase)}</span>
                                        </div>
                                        <div className="qcp-summary__row" style={{ color: '#5f6368', fontSize: '0.9rem' }}>
                                            <span>Estimated GST</span>
                                            <span>{fmt(totalGst)}</span>
                                        </div>
                                    </div>
                                    <div className="qcp-summary__total" style={{ marginTop: '0.5rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                                        <span>Total Amount</span>
                                        <span>{fmt(totalAmount)}</span>
                                    </div>
                                </div>

                                {error && <div className="qcp-error">{error}</div>}

                                <div className="qcp-actions">
                                    <button className="qcp-btn qcp-btn--ghost" onClick={() => { setStep(1); setTouched({}); setError(''); }}>← Back</button>
                                    <button
                                        className="qcp-btn qcp-btn--ghost"
                                        onClick={saveDraft}
                                        disabled={isSavingDraft}
                                    >
                                        {isSavingDraft ? 'Saving…' : '💾 Save Draft'}
                                    </button>
                                    <button className="qcp-btn qcp-btn--primary" onClick={handleSubmit} disabled={isSubmitting}>
                                        {isSubmitting ? 'Creating…' : '🚀 Create Curated Cart'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && createdCart && (
                        <div className="qcp-body qcp-body--centered">
                            <div className="qcp-panel qcp-panel--success">
                                <div className="qcp-success-icon">✓</div>
                                <h2 className="qcp-success-title">Curated Cart Created!</h2>

                                <div className="qcp-share-grid">
                                    <div className="qcp-share-field">
                                        <div className="qcp-share-field__label">Cart Name</div>
                                        <div className="qcp-share-field__val">
                                            <span>{createdCart.cartName}</span>
                                            <button className="qcp-copy-btn" onClick={() => copyToClipboard(createdCart.cartName, 'name')}>
                                                {copied === 'name' ? '✓' : 'Copy'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="qcp-share-msg-wrap">
                                    <textarea className="qcp-share-msg" readOnly value={shareText} rows={4} />
                                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem', width: '100%' }}>
                                        <button className="qcp-copy-btn" style={{ flex: 1 }} onClick={() => copyToClipboard(shareText, 'msg')}>
                                            {copied === 'msg' ? '✓ Copied!' : '📋 Copy Message'}
                                        </button>
                                        <a
                                            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                flex: 1, display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', gap: '0.4rem',
                                                padding: '0.5rem 1rem', borderRadius: '6px',
                                                background: '#25D366', color: '#fff',
                                                fontWeight: 600, fontSize: '0.875rem',
                                                textDecoration: 'none',
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                            Send on WhatsApp
                                        </a>
                                    </div>
                                </div>

                                <div className="qcp-actions qcp-actions--success">
                                    <button className="qcp-btn qcp-btn--ghost" onClick={() => {
                                        if (onDone) { onDone(); return; }
                                        setMode('list'); setStep(1); setCartItems([]);
                                        setCartName(''); setQuoteCreatedFor(''); setClientEmail('');
                                        setEyantraPocEmail(user?.email || ''); setEmsEnquiryId('');
                                        setCreatedCart(null); setError(''); setTouched({});
                                    }}>← View All Carts</button>
                                    <button className="qcp-btn qcp-btn--accent" onClick={() => navigate(`/shared-cart?id=${createdCart.id}`)}>
                                        Preview →
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default QuoteCartPage;
